/**
 * Tickets API service
 * Feature: 015-manual-crud-entities - User Story 3
 * Feature: 020-aws-production-readiness - Updated to route through API Gateway
 */

import axios from 'axios';
const PHONE_SANITIZATION_REGEX = /[^\d+]/g;

import type {
  Ticket,
  CreateTicketRequest,
  UpdateTicketRequest,
} from '../types/ticket';

// Create axios instance with auth headers - routes through API Gateway at /api
const axiosInstance = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('id_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data) {
    // 1. If it's a flat ticket field
    if (typeof config.data.phone === 'string') {
      config.data.phone = config.data.phone.replace(PHONE_SANITIZATION_REGEX, '');
    }
    // 2. Since this is an "Account Association" card inside a ticket, check for nested objects:
    if (config.data.newAccount && typeof config.data.newAccount.phone === 'string') {
      config.data.newAccount.phone = config.data.newAccount.phone.replace(PHONE_SANITIZATION_REGEX, '');
    }
  }

  return config;
});

/**
 * Create a new ticket with optional account creation
 * Supports two modes:
 * 1. Link to existing account (provide accountId)
 * 2. Create new account inline (provide newAccount)
 */
export async function createTicket(request: CreateTicketRequest): Promise<Ticket> {
  const response = await axiosInstance.post<Ticket>('/tickets', request);
  return response.data;
}

/**
 * Get ticket by ID
 * @returns Ticket with ETag in response headers
 */
export async function getTicketById(id: string): Promise<{ ticket: Ticket; etag: string }> {
  const response = await axiosInstance.get<Ticket>(`/tickets/${id}`);
  
  const etag = response.headers['etag'] || '';
  return { ticket: response.data, etag };
}

/**
 * Update an existing ticket
 * @param id Ticket ID (Guid)
 * @param request Update payload with all required fields
 * @param etag ETag from previous GET/POST for optimistic concurrency control
 * @returns Updated ticket with new ETag
 * @throws 412 Precondition Failed if ticket was modified by another user
 */
export async function updateTicket(
  id: string,
  request: UpdateTicketRequest,
  etag?: string
): Promise<{ ticket: Ticket; etag: string }> {
  const headers: Record<string, string> = {};
  if (etag) {
    headers['If-Match'] = etag;
  }

  const response = await axiosInstance.put<Ticket>(
    `/tickets/${id}`,
    request,
    { headers }
  );

  const newEtag = response.headers['etag'] || '';
  return { ticket: response.data, etag: newEtag };
}

/**
 * List tickets with optional filtering
 */
export async function listTickets(params?: {
  page?: number;
  pageSize?: number;
  accountId?: string;
  status?: number;
}): Promise<{ items: Ticket[]; total: number }> {
  const response = await axiosInstance.get<{ items: Ticket[]; total: number }>(
    '/tickets',
    { params }
  );
  return response.data;
}

/**
 * Associate or unassociate a client with a ticket (Feature 018 US1)
 * @param ticketId Ticket ID (Guid)
 * @param clientId Account ID to associate (null to unassociate)
 * @param etag ETag from previous GET for optimistic concurrency control
 * @returns Success response with updated timestamp
 * @throws 409 Conflict if ticket was modified by another user
 */
export async function associateTicketWithClient(
  ticketId: string,
  clientId: string | null,
  etag?: string
): Promise<{ ticketId: string; accountId: string | null; updatedAt: string; message: string }> {
  const headers: Record<string, string> = {};
  if (etag) {
    headers['If-Match'] = etag;
  }

  const response = await axiosInstance.patch(
    `/tickets/${ticketId}/client`,
    { clientId },
    { headers }
  );

  return response.data;
}

/**
 * Update ticket status (Feature 019 US2/P2)
 * @param ticketId Ticket ID (Guid)
 * @param status New status value
 * @returns Updated ticket info with status and timestamps
 */
export async function updateTicketStatus(
  ticketId: string,
  status: string
): Promise<{
  ticketId: string;
  status: string;
  updatedAt: string;
  resolvedAt?: string | null;
  closedAt?: string | null;
}> {
  const response = await axiosInstance.patch(
    `/tickets/${ticketId}/status`,
    { status }
  );

  return response.data;
}
